import { memo, useMemo, useEffect, useState } from "react";
import { ArrowLeft, Star, Package, Clock } from "lucide-react";
import { AdCard } from "@/app/components/ad-card";
import type { Ad } from "@/app/components/ad-card";
import { BottomNav } from "./bottom-nav";
import * as authClient from "@/app/api/auth-client";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";

interface SellerProfilePageProps {
  sellerId: string;
  sellerName: string | null;
  onBack: () => void;
  onAdClick: (ad: Ad) => void;
  onToggleFavorite?: (id: string) => void;
  isFavorite?: (id: string) => boolean;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export const SellerProfilePage = memo(function SellerProfilePage({
  sellerId,
  sellerName,
  onBack,
  onAdClick,
  onToggleFavorite,
  isFavorite,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: SellerProfilePageProps) {
  const accountNav = useAccountNavEnabled();
  const [user, setUser] = useState<authClient.PublicUserResponse | null>(null);
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      authClient.getUserPublic(sellerId),
      authClient.getAdsBySeller(sellerId),
    ])
      .then(([userData, adsList]) => {
        if (cancelled) return;
        setUser(userData);
        setAds(adsList);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? "Erro ao carregar perfil");
          setUser(null);
          setAds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  const displayName = (user?.display_name?.trim() || sellerName || "Vendedor").trim() || "Vendedor";
  const memberSinceYear = user?.member_since_year;
  const profileImageUrl = user?.profile_image_url?.trim() || undefined;

  const sellerAdsSorted = useMemo(() => {
    return [...ads].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [ads]);

  const initials = useMemo(() => {
    return displayName
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [displayName]);

  const reputation = 1;
  const totalSales = 0;

  if (error) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Voltar">
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl text-white">Perfil do Vendedor</h1>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-muted-foreground">{error}</p>
        </div>
        <BottomNav
          activeTab={activeTab}
          onTabChange={onTabChange}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Voltar">
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl text-white">Perfil do Vendedor</h1>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
        <BottomNav
          activeTab={activeTab}
          onTabChange={onTabChange}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Perfil do Vendedor</h1>
              <p className="text-sm text-white/80">{displayName}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
          <div className="flex items-start gap-4 mb-6">
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt=""
                className="w-20 h-20 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-2xl font-medium shrink-0">
                {initials}
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-2xl mb-2">{displayName}</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <Clock className="w-4 h-4" />
                <span>
                  Membro desde {memberSinceYear ? String(memberSinceYear) : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= reputation
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium">{reputation}</span>
                <span className="text-sm text-muted-foreground">
                  ({totalSales} vendas)
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Package className="w-4 h-4 text-primary" />
              </div>
              <p className="text-2xl font-medium text-primary">{sellerAdsSorted.length}</p>
              <p className="text-xs text-muted-foreground">Anúncios Ativos</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Star className="w-4 h-4 text-yellow-400" />
              </div>
              <p className="text-2xl font-medium">{reputation}</p>
              <p className="text-xs text-muted-foreground">Avaliação</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Clock className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-2xl font-medium">{totalSales}</p>
              <p className="text-xs text-muted-foreground">Vendas</p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-medium">
              Anúncios de {displayName.split(" ")[0] || displayName}
            </h3>
            <span className="text-sm text-muted-foreground">
              {sellerAdsSorted.length} {sellerAdsSorted.length === 1 ? "anúncio" : "anúncios"}
            </span>
          </div>

          {sellerAdsSorted.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                Este vendedor não possui anúncios ativos no momento.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {sellerAdsSorted.map((ad) => (
                <AdCard
                  key={ad.id}
                  ad={ad}
                  isFavorite={isFavorite?.(ad.id)}
                  onToggleFavorite={accountNav ? onToggleFavorite : undefined}
                  onAdClick={() => onAdClick(ad)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
});
